import React, { useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Paper,
  Grid,
  Divider,
  FormHelperText
} from '@mui/material';
import { ContigSelector } from '../ContigSelector';

interface FormValues {
  contigs?: string;
  hotspot_residues?: string;
  model_type?: 'default' | 'beta';
  noise_scale?: number;
  [key: string]: unknown;
}

interface ErrorState {
  contigs?: string;
  hotspot_residues?: string;
  [key: string]: string | undefined;
}

interface BinderDesignFormProps {
  formValues: FormValues;
  handleChange: (field: string, value: unknown) => void;
  errors: ErrorState;
  pdbContent: string | null;
}

const BinderDesignForm: React.FC<BinderDesignFormProps> = ({
  formValues,
  handleChange,
  errors,
  pdbContent
}) => {
  // Debug: log when pdbContent changes
  useEffect(() => {
    console.log('BinderDesignForm pdbContent changed:', pdbContent ? 'PDB content available' : 'No PDB content');
  }, [pdbContent]);

  // Handle contig change from the ContigSelector
  const handleContigChange = (value: string) => {
    handleChange('contigs', value);
  };

  // Handle hotspot changes
  const handleHotspotChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    handleChange('hotspot_residues', value);
  };

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 3, mb: 3, bgcolor: 'background.paper' }}>
        <Typography variant="h6" gutterBottom color="primary">
          Binder Design Mode
        </Typography>
        
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Optimize your binder design strategy:</strong>
            <br /><br />
            When targeting large proteins (like receptors), focusing on just the binding region can drastically improve design efficiency. Use the Binding Pocket parameters to define only the specific area where binding should occur.
            <br /><br />
            <strong>Key challenge:</strong> When cropping the target protein, you may inadvertently expose interior residues that shouldn't be involved in binding. The model might incorrectly target these newly exposed areas.
            <br /><br />
            <strong>Solution:</strong> Specify "Hotspots" to guide the design process toward legitimate surface residues intended for interaction. This ensures the generated binders target functionally relevant sites.
          </Typography>
        </Alert>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                For the best binder design, consider:
                <ul>
                  <li>Select a well-defined binding pocket region</li>
                  <li>Specify key interface residues as hotspots if known</li>
                  <li>Keep the binding region compact for better results</li>
                </ul>
              </Typography>
            </Alert>
          </Grid>

          <Grid item xs={12}>
            {!pdbContent ? (
              <Alert severity="warning">
                You need to upload a PDB file to select contigs
              </Alert>
            ) : (
              <ContigSelector
                pdbContent={pdbContent}
                onChange={handleContigChange}
                value={formValues.contigs || ''}
                isBinderMode={true}
              />
            )}
            
            {errors.contigs && (
              <FormHelperText error>{errors.contigs}</FormHelperText>
            )}
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Hotspot Residues"
              variant="outlined"
              placeholder="e.g. A23,B45,C12"
              value={formValues.hotspot_residues || ''}
              onChange={handleHotspotChange}
              error={!!errors.hotspot_residues}
              helperText={
                errors.hotspot_residues || 
                "Specify important interface residues as ChainResidue (e.g., A23,B45). Leave empty if unsure."
              }
            />
          </Grid>
          
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom>
              Advanced Options
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="model-type-label">Model Type</InputLabel>
              <Select
                labelId="model-type-label"
                value={formValues.model_type || 'default'}
                label="Model Type"
                onChange={(e) => handleChange('model_type', e.target.value)}
              >
                <MenuItem value="default">Default</MenuItem>
                <MenuItem value="beta">Beta (Complex Design)</MenuItem>
              </Select>
              <FormHelperText>
                Beta model may give better results for complex binder designs
              </FormHelperText>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography gutterBottom>Noise Scale: {formValues.noise_scale || 1.0}</Typography>
            <Slider
              value={formValues.noise_scale || 1.0}
              onChange={(_, value) => handleChange('noise_scale', value as number)}
              step={0.1}
              min={0}
              max={1}
              valueLabelDisplay="auto"
            />
            <FormHelperText>
              Controls design diversity. Lower values (closer to 0) produce more conservative designs, higher values (closer to 1) allow more diversity.
            </FormHelperText>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default BinderDesignForm; 